import {Button, Paper, Input, NumberInput, TextInput, MultiSelect, Checkbox} from "@mantine/core";
import { Plus as IconPlus } from "@phosphor-icons/react";
import {useCallback, useContext, useEffect, useState} from "react";
import {Col, Row} from "../../components/Flex/Flex";
import MessageContext from "../../context/MessageContext.tsx";
import {useUser, useSWCLocation, useSystems} from "../../utils/api.ts";
import SearchSelect from "../../components/SearchSelect/SearchSelect.tsx";


const emptySystem = {id: '', x: null, y: null};

const getLocationSystem = (location: UserLocation, systems: System[]) => {
    if(location) {
        let id = '';
        if(location.system.attributes.uid && systems.find(s => s.id === location.system.attributes.uid)) {
            id = location.system.attributes.uid;
        }
        return {
            id,
            x: location.coordinates.galaxy.attributes.x,
            y: location.coordinates.galaxy.attributes.y,
        }
    }
    return emptySystem;
}


export interface SelectSystem {
    id: string;
    x: number|null;
    y: number|null;
}

interface AddRouteProps {
    onSave: any
}
export default function AddRoute({onSave}: AddRouteProps) {
    const {systems} = useSystems();
    const {user} = useUser();
    const {location} = useSWCLocation();

    const {error} = useContext(MessageContext);
    const [name, setName] = useState<string>('');
    const [start, setStart] = useState<SelectSystem>(emptySystem);
    const [end, setEnd] = useState<SelectSystem>(emptySystem);
    const [exclude, setExclude] = useState<string[]>([]);
    const [avoidSpacePvp, setAvoidSpacePvp] = useState<boolean>(false);

    const save = () => {
        if(end.id !== '' && start.id === end.id) {
            error('Invalid action', "You can't use the same system as both start and end")
            return;
        }

        onSave(start, end, name, exclude, avoidSpacePvp).then(() => {
            setStart(getLocationSystem(location, systems));
            setEnd(emptySystem);
            setName('');
        })
    };

    useEffect(() => {
        setStart(getLocationSystem(location, systems));
    }, [location, systems]);

    const systemSelectData = systems.map(s => ({value: s.id, label: s.name}));
    const excludeSelectData = systems.map(s => ({value: s.id, label: s.name}));

    const onCoordChangeStart = useCallback((type: 'x'|'y', val: number|string) => {
        const numVal = typeof val === 'string' ? (val === '' ? null : Number(val)) : val;
        setStart(prev => {
            let x = type==='x' ? numVal : prev.x;
            let y = type==='y' ? numVal : prev.y;

            let s = systems.find(s => s.galX == x && s.galY == y);
            if(s) {
                return {id: s.id, x, y};
            } else {
                return {id: '', x, y};
            }
        })
    }, [systems]);
    const onCoordChangeEnd = useCallback((type: 'x'|'y', val: number|string) => {
        const numVal = typeof val === 'string' ? (val === '' ? null : Number(val)) : val;
        setEnd(prev => {
            let x = type==='x' ? numVal : prev.x;
            let y = type==='y' ? numVal : prev.y;

            let s = systems.find(s => s.galX == x && s.galY == y);
            if(s) {
                return {id: s.id, x, y};
            } else {
                return {id: '', x, y};
            }
        })
    }, [systems]);

    const onSystemSelectStart = useCallback((val: string | null) => {
        let s = systems.find(s => s.id === val);
        if(s) {
            setStart({id: val!, x: s.galX, y: s.galY});
        }
    }, [systems]);
    const onSystemSelectEnd = useCallback((val: string | null) => {
        let s = systems.find(s => s.id === val);
        if(s) {
            setEnd({id: val!, x: s.galX, y: s.galY});
        }
    }, [systems]);


    return (
        <Paper shadow="xs" p="md" withBorder style={{marginTop: '0.5rem'}}>
            <h3 style={{marginTop: 0}}>Add new route</h3>
            <Row align="start" mb={2}>
                <Col basis="300px">
                    <SearchSelect label="Start System" data={systemSelectData} value={start.id} onChange={onSystemSelectStart} />
                    <Row style={{marginTop: '0.25rem'}} mb={0}>
                        <Col><NumberInput style={{width: 80}} value={start.x ?? ''} min={-500} max={500} onChange={(val) => onCoordChangeStart('x', val)} placeholder="X" /></Col>
                        <Col><NumberInput style={{width: 80}} value={start.y ?? ''} min={-500} max={500} onChange={(val) => onCoordChangeStart('y', val)} placeholder="Y" /></Col>
                    </Row>
                </Col>
                <Col basis="300px">
                    <SearchSelect label="Finish System" data={systemSelectData} value={end.id} onChange={onSystemSelectEnd} />
                    <Row style={{marginTop: '0.25rem'}} mb={0}>
                        <Col><NumberInput style={{width: 80}} value={end.x ?? ''} min={-500} max={500} onChange={(val) => onCoordChangeEnd('x', val)} placeholder="X" /></Col>
                        <Col><NumberInput style={{width: 80}} value={end.y ?? ''} min={-500} max={500} onChange={(val) => onCoordChangeEnd('y', val)} placeholder="Y" /></Col>
                    </Row>
                </Col>
                <Col>
                    <TextInput label="Custom Name" value={name} onChange={e => setName(e.target.value)} />
                </Col>
                <Col grow={1} shrink={1}>
                    <Input.Label>Exclude Systems</Input.Label>
                    <MultiSelect value={exclude} data={excludeSelectData} searchable onChange={val => setExclude(val)} maxValues={12} disabled={user?.subLevel === 0} />
                    <Checkbox mt="xs" label="Avoid space PvP systems" checked={avoidSpacePvp} onChange={e => setAvoidSpacePvp(e.currentTarget.checked)} />
                </Col>
                <Col grow={1} />
                <Col>
                    <Button leftSection={<IconPlus size={16}/>} disabled={!(start.x !== null && start.y !== null && end.x !== null && end.y !== null)} onClick={save}>Calculate best route</Button>
                </Col>
            </Row>

        </Paper>
    )
}
